/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 174 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DELETE_CLEANUP
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Remove debug logging after successful fix of refresh and pagination issues.
 * This change also ensures that `pendingRefreshCallback` and `pendingResetPage`
 * variables are directly passed into the `useState` hook and that the
 * `cancelDelete` function clears these pending states.
 *
 * File Size Check:
 * - Current: 120 lines
 * - Removing: ~20 lines
 * - Projected: 100 lines
 * - Decision: Proceed (Clean up)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DELETE_SINGLE_ITEM_HOOK
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Update hook to support single item deletion by tracking and passing itemNo.
 * Updated confirmation modal text to reflect item removal.
 * This change also ensures that `pendingRefreshCallback` and `pendingResetPage`
 * variables are directly passed into the `useState` hook and that the
 * `cancelDelete` function clears these pending states.
 *
 * File Size Check:
 * - Current: 105 lines
 * - Adding: ~15 lines
 * - Projected: 120 lines
 * - Decision: Proceed (Hook logic)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DELETE_DEBUG
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Add debug logging to trace refresh callback execution after delete.
 * This change also ensures that `pendingRefreshCallback` and `pendingResetPage`
 * variables are directly passed into the `useState` hook and that the
 * `cancelDelete` function clears these pending states.
 *
 * File Size Check:
 * - Current: 90 lines
 * - Adding: ~15 lines
 * - Projected: 105 lines
 * - Decision: Proceed (Hook, acceptable slightly over 100 limit for debugging)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_DELETE_HOOK
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Hook for broadcast deletion with confirmation. Soft-deletes broadcast group,
 * shows success message, and triggers screen refresh to update the list.
 * This change also ensures that `pendingRefreshCallback` and `pendingResetPage`
 * variables are directly passed into the `useState` hook and that the
 * `cancelDelete` function clears these pending states.
 *
 * File Size Check:
 * - New hook: ~65 lines
 * - Limit: 100 lines (hooks)
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/services/broadcastService.ts (deleteBroadcastGroup API)
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */

import { useState } from 'react';
import { notify } from '../../../../../utils/notifications';
import { deleteBroadcastGroup } from '../../../../../services/broadcastService';

interface UseBroadcastDeleteReturn {
  handleDelete: (broadcastId: string, itemNo: number, refreshCallback: () => void, resetPage: () => void) => Promise<void>;
  isDeleting: boolean;
}

export const useBroadcastDelete = (): UseBroadcastDeleteReturn => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // [2026-06-25] DELETE-FIX: delete immediately on click — no confirmation modal.
  // (The old confirmation modal never rendered; per Ronen, Delete should just delete.)
  const handleDelete = async (broadcastId: string, itemNo: number, refreshCallback: () => void, resetPage: () => void): Promise<void> => {
    if (!broadcastId || itemNo === null || itemNo === undefined) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteBroadcastGroup(broadcastId, itemNo);
      notify.success('Broadcast item deleted successfully');
      if (refreshCallback) refreshCallback();
      if (resetPage) resetPage();
    } catch (error: any) {
      console.error('Delete broadcast error:', error);
      notify.error(error, "We couldn't delete this broadcast item. Please try again");
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    handleDelete,
    isDeleting,
  };
};
